import { useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "antd";
import { Calendar, Check, HelpCircle, MessageCircle, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../components/language/language_selector";
import ONBOARDING_HELP_LINKS from "./onboardingLinks";
import { useOnboarding } from "../../../contexts/OnboardingContext";
import softcarLogo from "../../../assets/icons/Sofrcar-logo.svg";

const PROGRESS_RADIUS = 10;
const PROGRESS_C = 2 * Math.PI * PROGRESS_RADIUS;

const VISUAL_STEPS = [
    {
        keys: ["company-info", "locations"],
        titleKey: "sidebar.macro.companyInfoTitle",
        descriptionKey: "sidebar.macro.companyInfoDescription",
    },
    {
        keys: ["categories"],
        titleKey: "sidebar.macro.categoriesTitle",
        descriptionKey: "sidebar.macro.categoriesDescription",
    },
    {
        keys: ["add-car"],
        titleKey: "sidebar.macro.addVehicleTitle",
        descriptionKey: "sidebar.macro.addVehicleDescription",
    },
    {
        keys: ["stripe", "insurance"],
        titleKey: "sidebar.macro.paymentTitle",
        descriptionKey: "sidebar.macro.paymentDescription",
    },
    {
        keys: ["preferences"],
        titleKey: "sidebar.macro.preferencesTitle",
        descriptionKey: "sidebar.macro.preferencesDescription",
    },
];

function MacroProgressRing({ progress }) {
    const offset = PROGRESS_C * (1 - progress);
    return (
        <svg width="28" height="28" viewBox="0 0 28 28" className="shrink-0" aria-hidden>
            <circle cx="14" cy="14" r={PROGRESS_RADIUS} fill="none" stroke="#E5E7EB" strokeWidth="3" />
            <circle
                cx="14"
                cy="14"
                r={PROGRESS_RADIUS}
                fill="none"
                stroke="#5d5dff"
                strokeWidth="3"
                strokeDasharray={PROGRESS_C}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 14 14)"
            />
        </svg>
    );
}

MacroProgressRing.propTypes = {
    progress: PropTypes.number.isRequired,
};

function StepBadge({ active, completed, label }) {
    if (completed) {
        return (
            <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white"
                aria-hidden>
                <Check size={16} strokeWidth={2.5} />
            </div>
        );
    }
    if (active) {
        return (
            <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#5d5dff] text-sm font-semibold text-white"
                aria-hidden>
                {label}
            </div>
        );
    }
    return (
        <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-white"
            aria-hidden>
            {label}
        </div>
    );
}

StepBadge.propTypes = {
    active: PropTypes.bool.isRequired,
    completed: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
};

const OnboardingSidebar = ({ className }) => {
    const { t } = useTranslation("onboarding");
    const { currentStepKey } = useOnboarding();
    const [helpOpen, setHelpOpen] = useState(false);

    const activeVisualIndex = Math.max(
        VISUAL_STEPS.findIndex((step) => step.keys.includes(currentStepKey)),
        0,
    );
    const totalVisualSteps = VISUAL_STEPS.length;
    const progress = (activeVisualIndex + 1) / totalVisualSteps;
    const activeStep = VISUAL_STEPS[activeVisualIndex];

    const helpLinks = [
        { href: ONBOARDING_HELP_LINKS.watchVideo, icon: Play, label: t("sidebar.help.watchVideo") },
        { href: ONBOARDING_HELP_LINKS.whatsApp, icon: MessageCircle, label: t("sidebar.help.chatWhatsApp") },
        { href: ONBOARDING_HELP_LINKS.bookDemo, icon: Calendar, label: t("sidebar.help.bookDemo") },
    ];

    const stepIndicator = t("sidebar.macro.stepIndicator", {
        current: activeVisualIndex + 1,
        total: totalVisualSteps,
    });

    return (
        <div
            className={`flex flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 lg:min-h-[calc(100vh-3rem)] ${className || ""}`}>
            <div className="mb-4 flex items-center justify-between gap-3 lg:mb-8">
                <img src={softcarLogo} alt="SoftCar" className="h-6 w-auto" />
                <LanguageSelector variant="onboarding" />
            </div>

            {/* Mobile / tablet: collapsed step summary with a Help overlay trigger */}
            <div className="lg:hidden">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <MacroProgressRing progress={progress} />
                        <span className="text-sm font-semibold text-[#5d5dff]">{stepIndicator}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setHelpOpen(true)}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700">
                        <HelpCircle size={16} strokeWidth={2} aria-hidden />
                        {t("sidebar.help.trigger")}
                    </button>
                </div>
                <div className="mt-3">
                    <p className="text-sm font-semibold text-[#5d5dff]">{t(activeStep.titleKey)}</p>
                    <p className="mt-1 text-xs leading-relaxed text-gray-500">{t(activeStep.descriptionKey)}</p>
                </div>
            </div>

            {/* Desktop: full step list */}
            <div className="hidden min-h-0 flex-1 lg:block">
                <div className="mb-5 flex items-center gap-2">
                    <MacroProgressRing progress={progress} />
                    <span className="text-sm font-semibold text-[#5d5dff]">{stepIndicator}</span>
                </div>

                <ul className="flex flex-col gap-5">
                    {VISUAL_STEPS.map((step, index) => {
                        const active = index === activeVisualIndex;
                        const completed = index < activeVisualIndex;
                        return (
                            <li key={step.titleKey} className="flex gap-3">
                                <StepBadge active={active} completed={completed} label={String(index + 1)} />
                                <div className="min-w-0 pt-0.5">
                                    <p
                                        className={`text-sm font-semibold ${
                                            active ? "text-[#5d5dff]" : completed ? "text-gray-800" : "text-gray-400"
                                        }`}>
                                        {t(step.titleKey)}
                                    </p>
                                    <p className="mt-1 text-xs leading-relaxed text-gray-500">{t(step.descriptionKey)}</p>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* Desktop: always-visible help panel */}
            <div className="mt-8 hidden shrink-0 lg:block">
                <div className="rounded-xl bg-[#F7F7FB] p-4">
                    <p className="text-sm font-semibold text-gray-900">{t("sidebar.help.title")}</p>
                    <p className="mt-2 text-xs leading-relaxed text-gray-500">{t("sidebar.help.description")}</p>
                    <ul className="mt-4 flex flex-col gap-2.5">
                        {helpLinks.map(({ href, icon: Icon, label }) => (
                            <li key={label}>
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-medium text-[#5d5dff] hover:text-[#4b4bd9]">
                                    <Icon size={16} strokeWidth={2} aria-hidden />
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Mobile / tablet: help content shown as an overlay instead of an always-visible panel */}
            <Modal open={helpOpen} onCancel={() => setHelpOpen(false)} footer={null} centered>
                <div className="pt-2">
                    <p className="text-base font-semibold text-gray-900">{t("sidebar.help.title")}</p>
                    <p className="mt-2 text-sm leading-relaxed text-gray-500">{t("sidebar.help.description")}</p>
                    <ul className="mt-4 flex flex-col gap-3">
                        {helpLinks.map(({ href, icon: Icon, label }) => (
                            <li key={label}>
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-medium text-[#5d5dff] hover:text-[#4b4bd9]">
                                    <Icon size={18} strokeWidth={2} aria-hidden />
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </Modal>
        </div>
    );
};

OnboardingSidebar.propTypes = {
    className: PropTypes.string,
};

OnboardingSidebar.defaultProps = {
    className: "",
};

export default OnboardingSidebar;
